import type {
  RuleBlockComponentProps,
  ReferralSetting,
  RuleData,
} from 'src/types/referral-v2';
import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useDispatch, useSelector} from 'react-redux';
import {getRuleInfo} from 'src/components/referral-v2/common';
import {updateOneTimeSettings} from 'src/action-creators/referral-v2/referral-settings';
import {selectReferralSetting} from 'src/selectors/referral-settings';
import classify from 'src/utils/classify';
import {getRadioTimeSheetLogOption} from 'src/utils/referral-v2';
import {
  FormLabelSmall,
  SubTitleExtraSmall,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Dropdown} from '@spaced-out/ui-design-system/lib/components/Dropdown';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {Card} from '@spaced-out/ui-design-system/lib/components/Card';
import {
  RadioGroup,
  RadioButton,
} from '@spaced-out/ui-design-system/lib/components/RadioButton';
import Rule from 'src/components/referral-v2/payout-rule/rule';
import rulesCss from 'src/components/referral-v2/settings/settings.css';
import {type MenuOption} from '@spaced-out/ui-design-system';


const EndDateRuleBlock = ({
  getEntityOptionsByFieldType,
  handleUpdateRuleBlockData,
  handleSelectRule,
  handleAddRule,
  ruleBlockData,
  entityFields,
  onDeleteRule,
  onEditRule,
  disabled,
}: RuleBlockComponentProps) => {
  const {t} = useLabelTranslation();
  const radioTimeSheetLogOption = getRadioTimeSheetLogOption(t);
  const [hourlyRuleType, setHourlyRuleType] = React.useState(
    ruleBlockData?.rule_type === 'field' ? 'field' : 'timesheet',
  );

  const dispatch = useDispatch();
  const referralSettings: ReferralSetting | null | undefined = useSelector(
    selectReferralSetting,
  );

  const timeSheetLogType =
    referralSettings?.one_time_settings.timesheet_log_type ?? 'days';

  const handleConfigRule = (value: string) => {
    if (value === 'true') {
      handleUpdateRuleBlockData('end_date_rule', {
        rule_type: 'field',
        field: {
          field_name: '',
        },
      });
      return;
    } else {
      handleUpdateRuleBlockData('end_date_rule', null);
    }
  };

  const handleChangeRuleType = (value: string) => {
    let data = {rule_type: value};
    if (value === 'field') {
      // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'field' does not exist in type '{ rule_type: string; }'.
      data = {...data, field: {field_name: ''}};
    } else {
      // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'rule' does not exist in type '{ rule_type: string; }'.
      data = {...data, rule: []};
    }
    handleUpdateRuleBlockData('end_date_rule', data);
  };

  const handleChangeTimeSheetLogType = async (timeSheetLogType: MenuOption) => {
    const data = {
      rule_type: 'field',
      field: {field_name: ''},
    } as const;
    dispatch(
      // @ts-expect-error - TS2345 - Argument of type '{ timesheet_log_type: any; reward_for_existing_referral?: boolean; duplicate_profile_check_method?: string | undefined; should_pay_for_different_role?: boolean | undefined; payout_rule?: PayoutRuleData | null | undefined; }' is not assignable to parameter of type 'ReferralOneTimeSetting'.
      updateOneTimeSettings({
        ...referralSettings?.one_time_settings,
        timesheet_log_type: timeSheetLogType.key,
      }),
    );
    handleUpdateRuleBlockData('end_date_rule', data);
  };

  const handleChangeHourlyRuleType = (value: string) => {
    setHourlyRuleType(value);
    if (value === 'timesheet') {
      handleUpdateRuleBlockData('end_date_rule', {
        rule_type: '',
        field: {field_name: ''},
      });
    } else {
      handleUpdateRuleBlockData('end_date_rule', {
        rule_type: 'field',
        field: {field_name: ''},
      });
    }
  };

  return (
    <div className={classify(rulesCss.flexColumn, rulesCss.defaultGap)}>
      <RadioGroup
        align="horizontal-fixed"
        name="radio-group-1"
        onChange={handleConfigRule}
        classNames={{
          wrapper: rulesCss.ruleInput,
          radioGroup: rulesCss.radioGroup,
        }}
        value={ruleBlockData ? 'true' : 'false'}
      >
        <RadioButton disabled={disabled} value={'true'}>
          {t('Yes', 'Yes')}
        </RadioButton>
        <RadioButton disabled={disabled} value={'false'}>
          {t('No', 'No')}
        </RadioButton>
      </RadioGroup>
      {ruleBlockData && (
        <>
          <div className={rulesCss.defaultGap}>
            <FormLabelSmall color="secondary">{`${t(
              'What_is_the_period_defined_in',
              'What is the period defined in',
            )}? *`}</FormLabelSmall>
            <div>
              <Dropdown
                disabled={disabled}
                menu={{
                  options: radioTimeSheetLogOption,
                  selectedKeys: [timeSheetLogType],
                }}
                onChange={handleChangeTimeSheetLogType}
                required={true}
                size="small"
                dropdownInputText={
                  radioTimeSheetLogOption.find(
                    (option) => option.key === timeSheetLogType,
                  )?.label ?? 'Select'
                }
              />
            </div>
          </div>
          {timeSheetLogType === 'hours' ? (
            <>
              <FormLabelSmall color="secondary">{`${t(
                'Define_the_rule_which_helps_us_to_identify_the_number_of_hour_lapsed_for_the_referral_in_our_system',
                'Define the rule which helps us to identify the number of hour lapsed for the referral in our system',
              )}.`}</FormLabelSmall>
              <RadioGroup
                align="horizontal-fixed"
                name="radio-group-2"
                onChange={handleChangeHourlyRuleType}
                value={hourlyRuleType}
              >
                <RadioButton disabled={disabled} value={'timesheet'}>
                  {t('Manually_upload_timesheet', 'Manually upload timesheet')}
                </RadioButton>
                <RadioButton disabled={disabled} value={'field'}>
                  {t(
                    'Map_a_field_to_get_the_value_automatically',
                    'Map a field to get the value automatically',
                  )}
                </RadioButton>
              </RadioGroup>
              {hourlyRuleType === 'field' && (
                <div className={rulesCss.ruleInput}>
                  <Dropdown
                    required={true}
                    disabled={disabled}
                    size="small"
                    label={t('Field_name', 'Field name')}
                    onChange={(option) =>
                      handleSelectRule(option, 'end_date_rule', ruleBlockData)
                    }
                    dropdownInputText={
                      ruleBlockData.field?.field_name ?? 'Select'
                    }
                    menu={{
                      classNames: {
                        wrapper: rulesCss.option,
                        optionTextContainer: rulesCss.option,
                      },
                      options: getEntityOptionsByFieldType('number') ?? [],
                      selectedKeys: ruleBlockData.field?.field_name
                        ? [ruleBlockData.field.field_name]
                        : [],
                      allowSearch: true,
                      staticLabels: {
                        SEARCH_PLACEHOLDER: `${t('Search', 'Search')}...`,
                        RESULT: t('Result', 'Result'),
                        RESULTS: t('Results', 'Results'),
                      },
                    }}
                  />
                </div>
              )}
              {hourlyRuleType === 'timesheet' ? (
                <Card classNames={{wrapper: rulesCss.chip}}>
                  <SubTitleExtraSmall color="secondary">
                    {t(
                      'Manual_Input_will_be_required_to_confirm_payout_eligibility_you_can_upload_it_through_the_timesheet_feature_in_payout_section',
                      'Manual Input will be required to confirm payout eligibility, you can upload it through the timesheet feature in payout section',
                    )}
                  </SubTitleExtraSmall>
                </Card>
              ) : (
                ruleBlockData.field?.field_name && (
                  <Card classNames={{wrapper: rulesCss.chip}}>
                    <SubTitleExtraSmall color="secondary">
                      {`${t('We_will_use_the', 'We will use the')} "`}
                      {ruleBlockData.field?.field_name}
                      {`" ${t(
                        'field_value_to_get_the_number_of_hours_completed_payout_eligibility_will_depend_on_individual_program_level_input',
                        'field value to get the number of hours completed, payout eligibility will depend on individual program level input',
                      )}`}
                    </SubTitleExtraSmall>
                  </Card>
                )
              )}
            </>
          ) : (
            <>
              <Card classNames={{wrapper: rulesCss.chip}}>
                <SubTitleExtraSmall color="secondary">
                  {t(
                    'We_will_consider_the_referral_eligible_if_the_referral_hired_completes_the_number_of_days_as_per_the_configuration_in_the_referral_programs_the_default_is_90_days',
                    'We will consider the referral eligible if the referral hired completes the number of days as per the configuration in the referral programs. the default is 90 days',
                  )}
                </SubTitleExtraSmall>
              </Card>
              <RadioGroup
                align="horizontal-fixed"
                name="radio-group-2"
                onChange={handleChangeRuleType}
                value={ruleBlockData.rule_type ?? 'field'}
              >
                <RadioButton disabled={disabled} value={'field'}>
                  {t(
                    'Map_a_field_to_get_the_value_automatically',
                    'Map a field to get the value automatically',
                  )}
                </RadioButton>
                <RadioButton disabled={disabled} value={'event'}>
                  {t('Configure_rule', 'Configure rule')}
                </RadioButton>
              </RadioGroup>
              {ruleBlockData.rule_type === 'event' ? (
                <>
                  <FormLabelSmall color="secondary">{`${t(
                    'Define_the_rule_which_helps_us_to_identify_the_retiredcontract_end_date_of_the_referral_in_our_system',
                    'Define the rule which helps us to identify the retired/contract end date of the referral in our system',
                  )}: *`}</FormLabelSmall>
                  {ruleBlockData.rule?.map(
                    (payoutRule: RuleData, index: number) => {
                      const rule = entityFields.find(
                        (field) => field.value === payoutRule.field_name,
                      );
                      return (
                        <Rule
                          showAndText={index > 0}
                          disabled={disabled}
                          options={getEntityOptionsByFieldType()}
                          rule={rule}
                          fieldObject={payoutRule}
                          handleSelectRule={(option) =>
                            handleSelectRule(
                              option,
                              'end_date_rule',
                              ruleBlockData,
                              index,
                            )
                          }
                          onEdit={(ruleData) =>
                            onEditRule(
                              ruleData,
                              'end_date_rule',
                              ruleBlockData,
                              index,
                            )
                          }
                          onRemove={() =>
                            onDeleteRule(index, 'end_date_rule', ruleBlockData)
                          }
                        />
                      );
                    },
                  )}
                  <div className={rulesCss.ruleInputContainer}>
                    <Button
                      size="small"
                      iconLeftName="plus"
                      isFluid={false}
                      type="secondary"
                      disabled={disabled}
                      onClick={() =>
                        handleAddRule('end_date_rule', ruleBlockData)
                      }
                    >
                      {t('Add_a_new_clause', 'Add a new clause')}
                    </Button>
                  </div>
                  {ruleBlockData.rule &&
                    ruleBlockData.rule.filter((ruleObj) => ruleObj.field_name)
                      .length > 0 && (
                      <Card classNames={{wrapper: rulesCss.chip}}>
                        <SubTitleExtraSmall color="secondary">
                          {t(
                            'We_will_use_the_date_when_the_condition_defined_above_is_satisfied_to_identify_the_end_date_for_the_referral_candidate_In_this_case_when_the_field',
                            'We will use the date when the condition defined above is satisfied to identify the end date for the referral candidate. In this case, when the field',
                          )}
                          {getRuleInfo(ruleBlockData.rule)}
                        </SubTitleExtraSmall>
                      </Card>
                    )}
                </>
              ) : (
                <>
                  <div className={rulesCss.ruleInput}>
                    <Dropdown
                      required={true}
                      disabled={disabled}
                      size="small"
                      label={t('Field_name', 'Field name')}
                      onChange={(option) =>
                        handleSelectRule(option, 'end_date_rule', ruleBlockData)
                      }
                      dropdownInputText={
                        ruleBlockData.field?.field_name ?? 'Select'
                      }
                      menu={{
                        classNames: {
                          wrapper: rulesCss.option,
                          optionTextContainer: rulesCss.option,
                        },
                        options: getEntityOptionsByFieldType('date') ?? [],
                        selectedKeys: ruleBlockData.field?.field_name
                          ? [ruleBlockData.field.field_name]
                          : [],
                        allowSearch: true,
                        staticLabels: {
                          SEARCH_PLACEHOLDER: `${t('Search', 'Search')}...`,
                          RESULT: t('Result', 'Result'),
                          RESULTS: t('Results', 'Results'),
                        },
                      }}
                    />
                  </div>
                  {ruleBlockData.field?.field_name && (
                    <Card classNames={{wrapper: rulesCss.chip}}>
                      <SubTitleExtraSmall color="secondary">
                        {`${t(
                          'We_will_use_the_date_shown_in',
                          'We will use the date shown in',
                        )} "`}
                        {ruleBlockData.field?.field_name}
                        {`" ${t(
                          'field_to_identify_the_end_date_of_referral_candidate',
                          'field to identify the end date of referral candidate',
                        )}.`}
                      </SubTitleExtraSmall>
                    </Card>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default EndDateRuleBlock;
